import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PasswordResetPage({ accountType = 'client' }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const config = useMemo(() => {
    const isVendor = accountType === 'vendor';
    return {
      title: isVendor ? 'Reset Vendor Password' : 'Reset Password',
      subtitle: isVendor ? 'Choose a new password for your vendor portal.' : 'Choose a new password for your account.',
      endpoint: isVendor ? `${API}/vendor/reset-password` : `${API}/auth/reset-password`,
      backHref: isVendor ? '/vendor/auth' : '/client/auth',
      accentClass: isVendor ? 'bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white' : 'btn-primary',
      backLabel: isVendor ? 'Back to vendor sign in' : 'Back to sign in',
    };
  }, [accountType]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('This reset link is missing its token. Please request a new one.');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(config.endpoint, { token, new_password: password });
      toast.success('Your password has been reset successfully.');
      navigate(config.backHref);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 flex items-center justify-center" data-testid={`${accountType}-reset-password-page`}>
      <div className="container-wedding max-w-md">
        <div className="bg-white rounded-sm border border-stone-200 p-8 shadow-sm">
          <Link to={config.backHref} className="text-sm text-stone-500 hover:text-stone-700" data-testid={`${accountType}-reset-password-back-link`}>
            ← {config.backLabel}
          </Link>

          <div className="mt-4 mb-6">
            <h1 className="heading-md mb-2">{config.title}</h1>
            <p className="text-sm text-stone-600">{config.subtitle}</p>
          </div>

          {!token ? (
            <div className="rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid={`${accountType}-reset-password-missing-token`}>
              This reset link is incomplete. Please request a new password reset.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor={`${accountType}-new-password`}>New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id={`${accountType}-new-password`}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid={`${accountType}-reset-password-input`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    data-testid={`${accountType}-reset-password-toggle`}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor={`${accountType}-confirm-password`}>Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id={`${accountType}-confirm-password`}
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid={`${accountType}-reset-confirm-password-input`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    data-testid={`${accountType}-reset-confirm-password-toggle`}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className={`w-full ${config.accentClass}`} disabled={loading} data-testid={`${accountType}-reset-password-submit`}>
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save New Password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}